import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  getGoogleMapApiKey,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
} from '../../Utilities/Apikeys';

const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Start typing an address...',
}) => {
  const apiKey = getGoogleMapApiKey();
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const blurTimeoutRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const placesDivRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const getSessionToken = useCallback(() => {
    if (!window.google?.maps?.places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 220 && rect.top > spaceBelow;
    setMenuStyle({
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 2000,
      ...(openUpward
        ? { bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' }
        : { top: `${rect.bottom + 4}px`, bottom: 'auto' }),
    });
  }, []);

  const fetchSuggestions = useCallback((input) => {
    if (!isLoaded || !window.google?.maps?.places || !input || input.trim().length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: input.trim(),
        sessionToken: getSessionToken(),
        componentRestrictions: { country: 'in' },
      },
      (predictions, status) => {
        setLoading(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setSuggestions([]);
          setIsOpen(false);
          return;
        }
        setSuggestions(predictions);
        setIsOpen(true);
        setActiveIndex(-1);
      }
    );
  }, [getSessionToken, isLoaded]);

  useEffect(() => {
    const timer = setTimeout(() => fetchSuggestions(value || ''), 300);
    return () => clearTimeout(timer);
  }, [value, fetchSuggestions]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updateMenuPosition();
    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, suggestions, updateMenuPosition]);

  useEffect(() => () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  }, []);

  const selectPrediction = useCallback((prediction) => {
    if (!window.google?.maps?.places) return;

    if (!placesDivRef.current) {
      placesDivRef.current = document.createElement('div');
    }

    const placesService = new window.google.maps.places.PlacesService(placesDivRef.current);
    placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'name'],
        sessionToken: getSessionToken(),
      },
      (place, status) => {
        sessionTokenRef.current = null;
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
          onChange(prediction.description);
          setIsOpen(false);
          setSuggestions([]);
          return;
        }

        const address = place.formatted_address || prediction.description;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        onChange(address);
        if (onPlaceSelect) {
          onPlaceSelect({ address, lat, lng });
        }
        setIsOpen(false);
        setSuggestions([]);
      }
    );
  }, [getSessionToken, onChange, onPlaceSelect]);

  const handleKeyDown = (event) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectPrediction(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <Box ref={containerRef} position="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        onBlur={() => {
          blurTimeoutRef.current = setTimeout(() => setIsOpen(false), 180);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && (
        <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
          <Spinner size="xs" color="brand.500" />
        </Box>
      )}
      {isOpen && suggestions.length > 0 && menuStyle && (
        <Portal>
          <List
            style={menuStyle}
            bg="white"
            border="1px solid"
            borderColor="slate.200"
            borderRadius="lg"
            boxShadow="lg"
            maxH="240px"
            overflowY="auto"
            py={1}
          >
            {suggestions.map((item, index) => (
              <ListItem
                key={item.place_id}
                px={3}
                py={2}
                cursor="pointer"
                bg={index === activeIndex ? 'brand.50' : 'white'}
                _hover={{ bg: 'brand.50' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectPrediction(item)}
              >
                <Text fontSize="sm" fontWeight="500" color="slate.800" noOfLines={1}>
                  {item.structured_formatting?.main_text || item.description}
                </Text>
                {item.structured_formatting?.secondary_text && (
                  <Text fontSize="xs" color="slate.500" noOfLines={1}>
                    {item.structured_formatting.secondary_text}
                  </Text>
                )}
              </ListItem>
            ))}
          </List>
        </Portal>
      )}
    </Box>
  );
};

export default AddressAutocomplete;
